<template>
  <v-data-table
    :items-per-page="20"
    :headers="headers"
    sort-asc-icon="$chevron-down"
    sort-desc-icon="$chevron-up"
    :items="documents"
    must-sort
    :sort-by="[{ key: sortBy, order: sortDesc ? 'desc' : 'asc' }]"
    @update:sort-by="handleSortBy"
  >
    <!-- eslint-disable vue/valid-v-slot -->
    <template #item.title="{ item }">
      <NoEditorWrapper>
        <router-link
          :to="{ name: 'editor-view', params: { id: item.job_id } }"
          @click.stop.prevent="$router.replace({
            query: { ...$route.query, preview: item.job_id, type: 'document' },
          })"
        >
          <strong class="nowrap job-title">{{ item.title }}</strong>
          <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
          <a
            :href="`/view/${item.job_id}`"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-1"
            :aria-label="`Open ${item.title}`"
            @click.stop
          >
            <v-icon
              icon="material-symbols-light:edit-square-outline"
              size="16"
              color="iconDefault"
            />
          </a>
        </router-link>
      </NoEditorWrapper>
    </template>

    <template #item.location_clean="{ item }">
      <div class="nowrap">
        {{ item.location_clean || 'n/a' }}
      </div>
    </template>

    <template #item.coordinators="{ item }">
      <div
        v-for="coordinator in item.coordinators"
        :key="coordinator"
        class="nowrap"
      >
        {{ coordinator }}
      </div>
    </template>

    <template #item.collaborators="{ item }">
      <div
        v-for="collaborator in item.collaborators"
        :key="collaborator"
        class="nowrap"
      >
        {{ collaborator }}
      </div>
    </template>

    <template #item.recruiters="{ item }">
      <div
        v-for="recruiter in item.recruiters"
        :key="recruiter"
        class="nowrap"
      >
        {{ recruiter }}
      </div>
    </template>

    <template #item.hiringManagers="{ item }">
      <div
        v-for="hiringManager in item.hiringManagers"
        :key="hiringManager"
        class="nowrap"
      >
        {{ hiringManager }}
      </div>
    </template>

    <template #item.score_total_capped="{ item }">
      <strong
        :style="{ color: getColor(item.score_total_capped) }"
        class="score"
      >
        {{ item.score_total_capped }}
      </strong>
    </template>

    <template #item.has_perks="{ item }">
      {{ item.has_perks ? 'Yes' : 'No' }}
    </template>

    <template #item.has_eeoc="{ item }">
      {{ item.has_eeoc ? 'Yes' : 'No' }}
    </template>

    <template #item.has_reporting_structure="{ item }">
      {{ item.has_reporting_structure ? 'Yes' : 'No' }}
    </template>

    <template #item.has_significant_title_issues="{ item }">
      {{ item.has_significant_title_issues ? 'Yes' : 'No' }}
    </template>
    <template #bottom>
      <span />
    </template>
  </v-data-table>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { colorMap } from '@/helpers/formatters';
import { useWeeklySnapshot } from '@/stores/insights/weeklySnapshot';
import { useFilters } from '@/stores/filters/filters';
import { useColors } from '@/composables/colors';
import { useAppAuthData } from '@/stores/app/authData';
import NoEditorWrapper from '@/components/editor/NoEditorWrapper.vue';

export default {
  name: 'WeeklySnapshotTable',
  components: { NoEditorWrapper },
  setup() {
    const {
      brandPrimary,
      grey800,
      red700,
      orange700,
      blue700,
      green700,
    } = useColors();

    return {
      brandPrimary,
      colorsToMap: {
        grey800,
        red700,
        orange700,
        blue700,
        green700,
      },
    };
  },
  computed: {
    ...mapState(useAppAuthData, ['integrated']),
    ...mapState(useWeeklySnapshot, ['documents', 'sortBy', 'sortDesc']),

    headers() {
      const { integrated } = this;

      return [
        { title: 'Job Title', value: 'title', sortable: true },
        {
          title: 'Req ID', value: 'ats_requisition_id', sortable: false, minWidth: '175px',
        },
        ...(integrated
          ? [
            { title: 'Recruiters', value: 'recruiters', sortable: false },
          ] : []
        ),
        { title: 'Location', value: 'location_clean', sortable: true },
        { title: 'Score', value: 'score_total_capped', sortable: true },
        { title: 'Days Live', value: 'days_published', sortable: true },
        ...(integrated
          ? [
            { title: 'Department', value: 'ats_department_name', sortable: true },
            { title: 'Coordinators', value: 'coordinators', sortable: false },
            { title: 'Hiring Managers', value: 'hiringManagers', sortable: false },
          ] : []
        ),
        { title: 'Revisions', value: 'total_revisions', sortable: true },
        { title: 'Total Editors', value: 'total_collaborators', sortable: true },
        { title: 'Editors', value: 'collaborators', sortable: false },
        ...(integrated
          ? [
            { title: 'Applications', value: 'total_applicants', sortable: true },
          ] : []
        ),
        { title: 'Words', value: 'total_words', sortable: true },
        { title: 'Benefits', value: 'has_perks', sortable: true },
        { title: 'Diversity', value: 'has_eeoc', sortable: true },
        { title: 'Reporting', value: 'has_reporting_structure', sortable: true },
        { title: 'Title Issues', value: 'has_significant_title_issues', sortable: true },
      ];
    },
  },
  methods: {
    ...mapActions(useWeeklySnapshot, ['updateSort']),
    ...mapActions(useFilters, ['sendFilterRequest']),
    handleSortBy(value) {
      if (value?.[0]) {
        this.updateSort({
          router: this.$router,
          newSortBy: value[0].key,
          newSortDesc: value[0].order === 'desc',
          sendFilterRequestAction: this.sendFilterRequest,
        });
      }
    },
    getColor(score) {
      if (!score || score === '0') {
        return this.brandPrimary;
      }
      return colorMap(score, this.colorsToMap);
    },
  },
};
</script>

<style scoped lang="less">
:deep(.nowrap), :deep(th) {
  white-space: nowrap;
}

:deep(.v-data-table-header__icon) {
  font-size: 12px !important;
  margin: 0px 0px 0px 3px;
  position: static;
}

:deep(.job-title) {
  color: @text-darkest;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

:deep(.score) {
  font-weight: 500;
}
</style>
